import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lightning, ArrowRight, X, Clock } from "@phosphor-icons/react";
import {
  CHROME_STORE_URL,
  EXTENSION_LIVE,
  EXTENSION_INSTALLED_LS_KEY,
  EXTENSION_BANNER_SNOOZED_LS_KEY,
} from "@/lib/extensionConstants";
import { isNative } from "@/lib/native";

/**
 * ExtensionLaunchBanner — Banner dashboard che invita l'agente a installare
 * la Chrome Extension "Pronto Publisher".
 *
 * 3 stati possibili:
 *   1. **Estensione già installata** → banner nascosto (rilevato via
 *      localStorage flag che il content script setta).
 *   2. **Estensione approvata su Store** (EXTENSION_LIVE = true) → banner
 *      verde "Installa l'estensione · 1 click su Idealista, Immobiliare,
 *      Casa.it, Subito".
 *   3. **In attesa di Google** (EXTENSION_LIVE = false) → banner avana
 *      "In arrivo · presto su Chrome Web Store" + link Landing per più info.
 *
 * Snooze: pulsante "Ricordamelo dopo" nasconde per 7 giorni (localStorage).
 *
 * Non usa l'endpoint backend `/extension/installed-status` perché vogliamo
 * latenza 0 al mount della dashboard. Il flag localStorage è settato dal
 * content script `dashboard-beacon.js` appena l'utente apre la dashboard
 * con l'estensione attiva.
 */
export default function ExtensionLaunchBanner() {
  const [hidden, setHidden] = useState(true);

  useEffect(() => {
    try {
      // iter r96b · Su app iOS/Android l'estensione Chrome è impossibile
      // da installare (Apple non ammette WebExtensions). Nascondiamo il
      // banner in radice invece di far scaricare qualcosa che non
      // funzionerà. Il PortaliTab già mostra un callout dedicato
      // "Da desktop" dentro il singolo annuncio.
      if (isNative) {
        setHidden(true);
        return;
      }
      // Già installato → niente banner
      if (localStorage.getItem(EXTENSION_INSTALLED_LS_KEY) === "1") {
        setHidden(true);
        return;
      }
      // Snoozato → niente banner fino alla scadenza
      const snoozeUntil = localStorage.getItem(EXTENSION_BANNER_SNOOZED_LS_KEY);
      if (snoozeUntil && Date.now() < Number(snoozeUntil)) {
        setHidden(true);
        return;
      }
      setHidden(false);
    } catch {
      setHidden(false);
    }
  }, []);

  const snooze7Days = () => {
    try {
      const week = 7 * 24 * 60 * 60 * 1000;
      localStorage.setItem(EXTENSION_BANNER_SNOOZED_LS_KEY, String(Date.now() + week));
    } catch { /* noop */ }
    setHidden(true);
  };

  if (hidden) return null;

  // ─── Stato "in attesa di Google" ──────────────────────────────────
  if (!EXTENSION_LIVE) {
    return (
      <div
        className="relative bg-[#FFF6E5] border border-[#F2D89E] rounded-md px-5 py-4 mb-6 pr-12"
        data-testid="extension-launch-banner-waitlist"
      >
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-[#F2D89E] flex-shrink-0 flex items-center justify-center">
            <Clock size={16} weight="bold" className="text-[#8B6914]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-semibold text-[#1C1C1A] mb-0.5">
              Pronto Publisher · in arrivo su Chrome
            </div>
            <p className="text-[12px] text-[#6B6A65] leading-snug">
              L'estensione che ti aiuta a compilare i form di Idealista e Immobiliare.it — vendita e affitto — è in revisione da Google. Iscriviti alla waitlist per essere avvisato appena è disponibile.
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-2">
              <Link
                to="/estensione"
                className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#8B6914] hover:text-[#5d4708] underline underline-offset-2"
                data-testid="extension-banner-waitlist-cta"
              >
                Scopri di più <ArrowRight size={11} weight="bold" />
              </Link>
              <button
                onClick={snooze7Days}
                className="text-[11.5px] text-[#9A968B] hover:text-[#6B6A65] underline-offset-2 hover:underline"
                data-testid="extension-banner-snooze"
              >
                Ricordamelo fra 7 giorni
              </button>
            </div>
          </div>
        </div>
        <button
          onClick={snooze7Days}
          className="absolute top-2 right-2 w-7 h-7 rounded-full text-[#9A968B] hover:text-[#1C1C1A] hover:bg-white/60 inline-flex items-center justify-center transition-colors"
          aria-label="Chiudi"
          title="Ricordamelo fra 7 giorni"
          data-testid="extension-banner-close"
        >
          <X size={13} weight="bold" />
        </button>
      </div>
    );
  }

  // ─── Stato "live su Store" ────────────────────────────────────────
  return (
    <div
      className="relative bg-[#F1F4ED] border border-[#C8CDB8] rounded-md px-3 py-3 sm:px-5 sm:py-4 mb-6 pr-10 sm:pr-12"
      data-testid="extension-launch-banner-live"
    >
      <div className="flex items-start gap-2.5 sm:gap-3">
        <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-[#7C8B6E] flex-shrink-0 flex items-center justify-center">
          <Lightning size={14} weight="fill" className="text-white sm:hidden" />
          <Lightning size={16} weight="fill" className="text-white hidden sm:block" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[12.5px] sm:text-[13px] font-semibold text-[#1C1C1A] mb-0.5 leading-tight">
            Estensione Chrome disponibile
          </div>
          <p className="hidden sm:block text-[12px] text-[#6B6A65] leading-snug">
            Su Idealista e Immobiliare.it Pronto compila i campi che riesce a mappare e tiene tutti i dati dell'annuncio nel pannello a destra — così niente più cambia-scheda.
          </p>
          <p className="sm:hidden text-[11.5px] text-[#6B6A65] leading-snug">
            Compila i campi su Idealista e Immobiliare.it.
          </p>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-2">
            <a
              href={CHROME_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#7C8B6E] hover:bg-[#5d6952] text-white text-[12px] font-semibold transition-colors"
              data-testid="extension-banner-live-cta"
            >
              Aggiungi a Chrome <ArrowRight size={12} weight="bold" />
            </a>
            <button
              onClick={snooze7Days}
              className="hidden sm:inline text-[11.5px] text-[#9A968B] hover:text-[#6B6A65] underline-offset-2 hover:underline"
              data-testid="extension-banner-snooze"
            >
              Ricordamelo dopo
            </button>
          </div>
        </div>
      </div>
      <button
        onClick={snooze7Days}
        className="absolute top-2 right-2 w-7 h-7 rounded-full text-[#9A968B] hover:text-[#1C1C1A] hover:bg-white/60 inline-flex items-center justify-center transition-colors"
        aria-label="Chiudi"
        title="Ricordamelo fra 7 giorni"
        data-testid="extension-banner-close"
      >
        <X size={13} weight="bold" />
      </button>
    </div>
  );
}
